import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import styles from './ProjectSelectionModal.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';
const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://wsticket.frozentoasty.de';

const ProjectSelectionModal = ({ isOpen, onClose, onSelectProject, currentProject, currentTask }) => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [step, setStep] = useState('project'); // 'project' or 'task'
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      setIsClosing(false);
      setSearchTerm('');
      setStep('project');
      setSelectedProject(null);
      setTasks([]);

      const socket = io(SERVER_URL);
      socket.on('projectsUpdated', fetchProjects);

      return () => {
        socket.disconnect();
      };
    }
  }, [isOpen]);

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        if (step === 'task') {
          // Go back to project selection
          setStep('project');
          setSelectedProject(null);
          setSearchTerm('');
        } else {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, step]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const fetchTasks = async (projectId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/tasks?projectId=${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTasks(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setStep('project');
      setSelectedProject(null);
      setTasks([]);
    }, 300);
  };

  const handleSelectProjectStep = async (project) => {
    setSelectedProject(project);
    setSearchTerm('');
    await fetchTasks(project._id);
    setStep('task');
  };

  const handleSelectTask = (task) => {
    onSelectProject({ project: selectedProject, task });
    handleClose();
  };

  const handleSelectNoTask = () => {
    onSelectProject({ project: selectedProject, task: null });
    handleClose();
  };

  const handleClearProject = () => {
    onSelectProject({ project: null, task: null });
    handleClose();
  };

  const handleBack = () => {
    setStep('project');
    setSelectedProject(null);
    setSearchTerm('');
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.clientName && project.clientName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredTasks = tasks.filter(task =>
    task.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className={`${styles.overlay} ${isClosing ? styles.closing : ''}`} onClick={handleClose}>
      <div className={`${styles.modal} ${isClosing ? styles.closing : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          {step === 'task' && (
            <button onClick={handleBack} className={styles.backButton}>
              <i className="bi bi-arrow-left"></i>
            </button>
          )}
          <h2>{step === 'project' ? 'Projekt auswählen' : 'Aufgabe auswählen'}</h2>
          <button onClick={handleClose} className={styles.closeButton}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder={step === 'project' ? 'Projekt suchen...' : 'Aufgabe suchen...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
            autoFocus
          />
          <i className="bi bi-search" style={{ position: 'absolute', right: '30px', top: '50%', transform: 'translateY(-50%)', color: '#888' }}></i>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              {step === 'project' ? 'Lade Projekte...' : 'Lade Aufgaben...'}
            </div>
          ) : (
            <>
              {step === 'project' ? (
                <>
                  {/* "Kein Projekt" Option */}
                  <div
                    className={`${styles.projectItem} ${!currentProject ? styles.selected : ''}`}
                    onClick={handleClearProject}
                  >
                    <div className={styles.projectName}>
                      <i className="bi bi-x-circle" style={{ marginRight: '8px', color: '#888' }}></i>
                      Kein Projekt
                    </div>
                    {!currentProject && (
                      <i className="bi bi-check-lg" style={{ color: '#4a9eff' }}></i>
                    )}
                  </div>

                  {/* Project List */}
                  {filteredProjects.length === 0 ? (
                    <div className={styles.noResults}>
                      Keine Projekte gefunden
                    </div>
                  ) : (
                    filteredProjects.map(project => (
                      <div
                        key={project._id}
                        className={`${styles.projectItem} ${currentProject?._id === project._id ? styles.selected : ''}`}
                        onClick={() => handleSelectProjectStep(project)}
                      >
                        <div className={styles.projectInfo}>
                          <div className={styles.projectName}>
                            📁 {project.name}
                          </div>
                          {project.clientName && (
                            <div className={styles.clientName}>
                              {project.clientName}
                            </div>
                          )}
                        </div>
                        <i className="bi bi-chevron-right" style={{ color: '#888' }}></i>
                      </div>
                    ))
                  )}
                </>
              ) : (
                <>
                  {/* "Keine Aufgabe" Option */}
                  <div
                    className={`${styles.projectItem} ${!currentTask ? styles.selected : ''}`}
                    onClick={handleSelectNoTask}
                  >
                    <div className={styles.projectName}>
                      <i className="bi bi-x-circle" style={{ marginRight: '8px', color: '#888' }}></i>
                      Keine Aufgabe
                    </div>
                    {!currentTask && (
                      <i className="bi bi-check-lg" style={{ color: '#4a9eff' }}></i>
                    )}
                  </div>

                  {/* Task List */}
                  {filteredTasks.length === 0 ? (
                    <div className={styles.noResults}>
                      Keine Aufgaben gefunden
                    </div>
                  ) : (
                    filteredTasks.map(task => (
                      <div
                        key={task._id}
                        className={`${styles.projectItem} ${currentTask?._id === task._id ? styles.selected : ''}`}
                        onClick={() => handleSelectTask(task)}
                      >
                        <div className={styles.projectInfo}>
                          <div className={styles.projectName}>
                            ✓ {task.name}
                          </div>
                          {task.description && (
                            <div className={styles.clientName}>
                              {task.description}
                            </div>
                          )}
                        </div>
                        {currentTask?._id === task._id && (
                          <i className="bi bi-check-lg" style={{ color: '#4a9eff' }}></i>
                        )}
                      </div>
                    ))
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectSelectionModal;
